import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  TemplateResponseDto,
  TemplateCreateDto,
  TemplateUpdateDto,
} from '../../types/api';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../config/api';

export type TemplateItem = TemplateResponseDto;

export interface TemplateState {
  loading: boolean;
  items: TemplateItem[];
  error: string | null;
  selectedTemplateId: string | null;
  isModalOpen: boolean;
  modalMode: 'add' | 'edit';
  operationLoading: {
    list: boolean;
    get: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  errors: {
    list: string | null;
    get: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
  };
}

const initialOperationLoading = {
  list: false,
  get: false,
  create: false,
  update: false,
  delete: false,
};

const initialErrors = {
  list: null as string | null,
  get: null as string | null,
  create: null as string | null,
  update: null as string | null,
  delete: null as string | null,
};

const initialState: TemplateState = {
  loading: false,
  items: [],
  error: null,
  selectedTemplateId: null,
  isModalOpen: false,
  modalMode: 'add',
  operationLoading: { ...initialOperationLoading },
  errors: { ...initialErrors },
};

export const getTemplates = createAsyncThunk(
  'template/getTemplates',
  async (_, { rejectWithValue }) => {
    try {
      return await apiGet<TemplateItem[]>('/api/Template');
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şablonlar yüklenirken hata oluştu');
    }
  }
);

export const getTemplate = createAsyncThunk(
  'template/getTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      return await apiGet<TemplateItem>(`/api/Template/${id}`);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şablon yüklenirken hata oluştu');
    }
  }
);

export const createTemplate = createAsyncThunk(
  'template/createTemplate',
  async (body: TemplateCreateDto, { rejectWithValue }) => {
    try {
      return await apiPost<TemplateItem>('/api/Template', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şablon oluşturulurken hata oluştu');
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'template/updateTemplate',
  async (
    { id, body }: { id: string; body: TemplateUpdateDto },
    { rejectWithValue }
  ) => {
    try {
      const item = await apiPatch<TemplateItem>(`/api/Template/${id}`, body);
      return { id, item };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şablon güncellenirken hata oluştu');
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'template/deleteTemplate',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiDelete(`/api/Template/${id}`);
      return id;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şablon silinirken hata oluştu');
    }
  }
);

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    setSelectedTemplateId: (state, action: PayloadAction<string | null>) => {
      state.selectedTemplateId = action.payload;
    },
    setTemplateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    setTemplateModalMode: (state, action: PayloadAction<'add' | 'edit'>) => {
      state.modalMode = action.payload;
    },
    openTemplateEditModal: (state, action: PayloadAction<string>) => {
      state.selectedTemplateId = action.payload;
      state.modalMode = 'edit';
      state.isModalOpen = true;
    },
    openTemplateAddModal: (state) => {
      state.selectedTemplateId = null;
      state.modalMode = 'add';
      state.isModalOpen = true;
    },
    resetTemplateSelection: (state) => {
      state.selectedTemplateId = null;
      state.isModalOpen = false;
      state.modalMode = 'add';
    },
    clearTemplateError: (state) => {
      state.error = null;
      state.errors = { ...initialErrors };
    },
    clearTemplateItems: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTemplates.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getTemplates.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = Array.isArray(action.payload) ? action.payload : [];
      state.errors.list = null;
    });
    builder.addCase(getTemplates.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Şablonlar yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    builder.addCase(getTemplate.pending, (state) => {
      state.operationLoading.get = true;
      state.errors.get = null;
    });
    builder.addCase(getTemplate.fulfilled, (state, action) => {
      state.operationLoading.get = false;
      const item = action.payload;
      const idx = state.items.findIndex((i) => i.id === item.id);
      if (idx >= 0) state.items[idx] = item;
      else state.items.push(item);
      state.errors.get = null;
    });
    builder.addCase(getTemplate.rejected, (state, action) => {
      state.operationLoading.get = false;
      state.errors.get = typeof action.payload === 'string' ? action.payload : 'Şablon yüklenirken hata oluştu';
    });

    builder.addCase(createTemplate.pending, (state) => {
      state.operationLoading.create = true;
      state.errors.create = null;
    });
    builder.addCase(createTemplate.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      const item = action.payload;
      if (item?.id && !state.items.some((i) => i.id === item.id)) state.items.push(item);
      state.errors.create = null;
    });
    builder.addCase(createTemplate.rejected, (state, action) => {
      state.operationLoading.create = false;
      state.errors.create = typeof action.payload === 'string' ? action.payload : 'Şablon oluşturulurken hata oluştu';
    });

    builder.addCase(updateTemplate.pending, (state) => {
      state.operationLoading.update = true;
      state.errors.update = null;
    });
    builder.addCase(updateTemplate.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      const { id, item } = action.payload;
      const idx = state.items.findIndex((i) => i.id === id);
      if (idx >= 0 && item) state.items[idx] = item;
      state.errors.update = null;
    });
    builder.addCase(updateTemplate.rejected, (state, action) => {
      state.operationLoading.update = false;
      state.errors.update = typeof action.payload === 'string' ? action.payload : 'Şablon güncellenirken hata oluştu';
    });

    builder.addCase(deleteTemplate.pending, (state) => {
      state.operationLoading.delete = true;
      state.errors.delete = null;
    });
    builder.addCase(deleteTemplate.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.errors.delete = null;
    });
    builder.addCase(deleteTemplate.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.errors.delete = typeof action.payload === 'string' ? action.payload : 'Şablon silinirken hata oluştu';
    });
  },
});

export const {
  setSelectedTemplateId,
  setTemplateModalOpen,
  setTemplateModalMode,
  openTemplateEditModal,
  openTemplateAddModal,
  resetTemplateSelection,
  clearTemplateError,
  clearTemplateItems,
} = templateSlice.actions;
export default templateSlice.reducer;
